import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { counsellingAPI, enrollmentsAPI, referenceAPI, batchesAPI } from '../services/api';
import Modal from '../components/Modal';
import LoadingSpinner from '../components/LoadingSpinner';
import MessageDisplay from '../components/MessageDisplay';
import FilterBar from '../components/FilterBar/FilterBar';
import DataCard from '../components/DataCard/DataCard';
import FormField from '../components/Form/FormField';
import FormSelect from '../components/Form/FormSelect';
import FormTextarea from '../components/Form/FormTextarea';
import FormSection from '../components/Form/FormSection';
import { formatDate } from '../utils/formatUtils';
import useAsyncOperation from '../hooks/useAsyncOperation';
import { normalizeApiListResponse } from '../utils/apiListNormalize';
import { formatBatchDisplayLabel, sortBatchesForDisplay } from '../utils/batchDisplayLabel';
import StaffPageShell from '../components/StaffPageShell';

const CounsellingSessionsPage = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [batches, setBatches] = useState([]);
  const [counselors, setCounselors] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [batchFilter, setBatchFilter] = useState('all');
  const { loading, error, success, execute, setError, setSuccess } = useAsyncOperation();
  const [filter, setFilter] = useState('all'); // all, follow-up-needed, no-follow-up
  const [showModal, setShowModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingSession, setEditingSession] = useState(null);
  const [formData, setFormData] = useState({
    enrollmentId: '',
    sessionDate: '',
    counselorId: '',
    remarks: '',
    followUpNeeded: false,
    followUpDate: ''
  });

  useEffect(() => {
    fetchReferenceData();
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [batchFilter]);

  const fetchReferenceData = async () => {
    try {
      const [enrollmentsRes, counselorsRes, batchesRes] = await Promise.all([
        enrollmentsAPI.getAll(),
        referenceAPI.getCounselors(),
        batchesAPI.getAll()
      ]);
      setEnrollments(normalizeApiListResponse(enrollmentsRes.data));
      setCounselors(normalizeApiListResponse(counselorsRes.data));
      setBatches(normalizeApiListResponse(batchesRes.data));
    } catch (err) {
      console.error('Error fetching reference data:', err);
    }
  };

  const fetchSessions = async () => {
    const params =
      batchFilter && batchFilter !== 'all' ? { batchId: batchFilter } : undefined;
    const response = await execute(async () => await counsellingAPI.getAll(params));
    if (response?.data !== undefined) {
      setSessions(normalizeApiListResponse(response.data));
    }
  };

  const handleCreate = () => {
    setEditingSession(null);
    setFormData({
      enrollmentId: '',
      sessionDate: '',
      counselorId: '',
      remarks: '',
      followUpNeeded: false,
      followUpDate: ''
    });
    setShowModal(true);
  };

  const handleEdit = (session) => {
    setEditingSession(session);
    setFormData({
      enrollmentId: session.enrollmentId?.toString() || '',
      sessionDate: session.sessionDate ? session.sessionDate.split('T')[0] : '',
      counselorId: session.counselorId?.toString() || '',
      remarks: session.remarks || '',
      followUpNeeded: session.followUpNeeded || false,
      followUpDate: session.followUpDate ? session.followUpDate.split('T')[0] : ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const response = await execute(async () => {
      if (editingSession) {
        return await counsellingAPI.update(editingSession.id, formData);
      } else {
        return await counsellingAPI.create(formData);
      }
    }, { 
      successMessage: `Counselling session ${editingSession ? 'updated' : 'created'} successfully!` 
    });
    
    if (response) {
      setShowModal(false);
      setEditingSession(null);
      fetchSessions();
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;

    const response = await execute(
      async () => await counsellingAPI.delete(deleteConfirm.id),
      { successMessage: 'Counselling session deleted successfully!' }
    );
    
    if (response) {
      setDeleteConfirm(null);
      fetchSessions();
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const filteredSessions = sessions.filter(session => {
    // Filter by follow-up status (only if filter is not 'all')
    if (filter && filter !== 'all') {
      if (filter === 'follow-up-needed' && !session.followUpNeeded) return false;
      if (filter === 'no-follow-up' && session.followUpNeeded) return false;
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const studentName = session.enrollment?.student?.fullName?.toLowerCase() || '';
      const counselorName = session.counselor?.counselorName?.toLowerCase() || '';
      const remarks = session.remarks?.toLowerCase() || '';
      const batchName = (
        session.batch?.batchName ||
        session.enrollment?.batch?.batchName ||
        ''
      ).toLowerCase();
      if (!studentName.includes(term) && !counselorName.includes(term) && !remarks.includes(term) && !batchName.includes(term)) {
        return false;
      }
    }

    return true;
  });

  return (
    <StaffPageShell title="Counselling Sessions" wide>
        <div className="flex justify-end items-center mb-6">
          <button
            onClick={handleCreate}
            className="px-6 py-2 rounded-lg font-medium theme-button-primary flex items-center gap-2"
          >
            <span>+</span> Add Session
          </button>
        </div>

        <MessageDisplay message={error || success} />

        {/* Filters */}
        <FilterBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search by student name, counselor, remarks, or batch..."
          filters={[
            {
              name: 'batchFilter',
              label: 'Batch',
              value: batchFilter,
              onChange: setBatchFilter,
              type: 'select',
              options: [
                { value: 'all', label: 'All batches' },
                ...sortBatchesForDisplay(batches).map((b) => ({
                    value: String(b.id),
                    label: formatBatchDisplayLabel(b),
                  }))
              ]
            },
            {
              name: 'filter',
              label: 'Filter by Follow-up',
              value: filter,
              onChange: setFilter,
              type: 'select',
              options: [
                { value: 'all', label: 'All Sessions' },
                { value: 'follow-up-needed', label: 'Follow-up Needed' },
                { value: 'no-follow-up', label: 'No Follow-up' }
              ]
            }
          ]}
        />

        {loading && !sessions.length ? (
          <LoadingSpinner message="Loading sessions..." />
        ) : filteredSessions.length === 0 ? (
          <div className="text-center py-12 theme-card rounded-lg">
            <p className="text-lg theme-text-secondary mb-4">
              {searchTerm ? 'No sessions found matching your search' : 'No counselling sessions found'}
            </p>
            {!searchTerm && (
              <button
                onClick={handleCreate}
                className="px-6 py-2 rounded-lg font-medium theme-button-primary"
              >
                Add Your First Session
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {filteredSessions.map((session) => (
              <DataCard
                key={session.id}
                title={session.enrollment?.student?.fullName || 'Unknown Student'}
                badges={session.followUpNeeded ? [
                  { label: 'Follow-up Needed', className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' }
                ] : []}
                metadata={[
                  { label: 'Counselor', value: session.counselor?.counselorName || 'N/A' },
                  { label: 'Session Date', value: formatDate(session.sessionDate) },
                  {
                    label: 'Batch',
                    value: session.batch?.batchName || session.enrollment?.batch?.batchName || 'N/A'
                  },
                  {
                    label: 'Course',
                    value:
                      session.batch?.course?.courseName ||
                      session.enrollment?.batch?.course?.courseName ||
                      'N/A'
                  }
                ]}
                actions={[
                  { label: 'Edit', onClick: () => handleEdit(session), variant: 'edit' },
                  { label: 'Delete', onClick: () => setDeleteConfirm(session), variant: 'delete' }
                ]}
              >
                {session.followUpDate && (
                  <div className="mt-2 text-sm">
                    <span className="theme-text-secondary">Follow-up Date:</span>
                    <span className="ml-2 theme-text font-medium text-orange-600 dark:text-orange-400">
                      {formatDate(session.followUpDate)}
                    </span>
                  </div>
                )}
                {session.remarks && (
                  <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <div className="text-sm font-medium theme-text-secondary mb-1">Remarks</div>
                    <p className="theme-text text-sm">{session.remarks}</p>
                  </div>
                )}
              </DataCard>
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        <Modal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingSession(null);
          }}
          title={editingSession ? 'Edit Counselling Session' : 'Add New Counselling Session'}
          size="md"
        >
          <>
            <form id="counselling-form" onSubmit={handleSubmit} className="space-y-6">
              {/* Session Information Section */}
              <FormSection title="Session Information">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormSelect
                    label="Student Enrollment"
                    name="enrollmentId"
                    value={formData.enrollmentId}
                    onChange={(e) => setFormData({ ...formData, enrollmentId: e.target.value })}
                    options={enrollments}
                    placeholder="Select Enrollment"
                    required
                    disabled={!!editingSession}
                    helpText={editingSession ? 'Enrollment cannot be changed after creation' : undefined}
                    getOptionLabel={(option) => `${option.student?.fullName || 'Unknown'} - ${option.batch?.batchName || 'N/A'}`}
                  />

                  <FormSelect
                    label="Counselor"
                    name="counselorId"
                    value={formData.counselorId}
                    onChange={(e) => setFormData({ ...formData, counselorId: e.target.value })}
                    options={counselors}
                    placeholder="Select Counselor"
                    required
                    getOptionLabel={(option) => option.counselorName}
                  />

                  <FormField
                    label="Session Date"
                    name="sessionDate"
                    type="date"
                    value={formData.sessionDate}
                    onChange={(e) => setFormData({ ...formData, sessionDate: e.target.value })}
                    required
                  />
                </div>
              </FormSection>

              {/* Session Details Section */}
              <FormSection title="Session Details">
                <FormTextarea
                  label="Remarks"
                  name="remarks"
                  value={formData.remarks}
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                  rows={4}
                  placeholder="Enter session remarks and notes..."
                />
              </FormSection>

              {/* Follow-up Information Section */}
              <FormSection title="Follow-up Information">
                <div className="space-y-4">
                  <label className="flex items-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.followUpNeeded}
                      onChange={(e) => setFormData({ ...formData, followUpNeeded: e.target.checked })}
                      className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-2 focus:ring-purple-500 cursor-pointer"
                    />
                    <span className="ml-3 text-gray-900 dark:text-gray-100 font-semibold flex items-center gap-2">
                      <span>📅</span> Follow-up Needed
                    </span>
                  </label>
                  {formData.followUpNeeded && (
                    <FormField
                      label="Follow-up Date"
                      name="followUpDate"
                      type="date"
                      value={formData.followUpDate}
                      onChange={(e) => setFormData({ ...formData, followUpDate: e.target.value })}
                    />
                  )}
                </div>
              </FormSection>
            </form>
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingSession(null);
                    }}
                    className="px-6 py-2.5 rounded-lg font-medium theme-button-secondary transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    form="counselling-form"
                    disabled={loading}
                    className="px-6 py-2.5 rounded-lg font-medium bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        {editingSession ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      <>
                        {editingSession ? (
                          <>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Update Session
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Create Session
                          </>
                        )}
                      </>
                    )}
                  </button>
                </div>
          </>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={!!deleteConfirm}
          onClose={() => setDeleteConfirm(null)}
          title="Delete Counselling Session"
          size="sm"
        >
          <p className="text-gray-700 dark:text-gray-200 mb-4">
            Are you sure you want to delete the counselling session for <strong>{deleteConfirm?.enrollment?.student?.fullName || 'this student'}</strong>?
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            <strong>Date:</strong> {formatDate(deleteConfirm?.sessionDate)}<br />
            <strong>Counselor:</strong> {deleteConfirm?.counselor?.counselorName || 'N/A'}
          </p>
          <div className="flex space-x-4">
            <button
              onClick={handleDelete}
              disabled={loading}
              className="flex-1 px-4 py-2 rounded-lg font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
            >
              {loading ? 'Deleting...' : 'Delete'}
            </button>
            <button
              onClick={() => setDeleteConfirm(null)}
              className="flex-1 px-4 py-2 rounded-lg font-medium theme-button-secondary"
            >
              Cancel
            </button>
          </div>
        </Modal>
    </StaffPageShell>
  );
};

export default CounsellingSessionsPage;
